import React, { useState, useEffect, useCallback, useRef } from 'react';
import SearchableSelect from './SearchableSelect';
import OpenFilterLink from './OpenFilterLink';

// Shared by the panel wizard (ConfigPage) and Describe-a-panel (PanelFromText), so both pick a
// filter from the same GetFilterList-backed list.

/**
 * A filter input that upgrades to a searchable dropdown WHEN the tenant has the
 * GetFilterList API (see backend GET /api/config/filters). It always writes the plain
 * filter ID back through `onChange`, so nothing downstream changes — the combo is just a
 * nicer way to pick the same number. Falls back to a manual number input whenever:
 *   - no project/type is chosen yet (nothing to scope by),
 *   - the tenant lacks the API (`available: false`),
 *   - filters are still loading, or the API returned no filters for this pair.
 * `cache`/`ensure` come from useFilterCache() so every field shares one fetch per (project,type).
 * `itemTypeLabel` is the type's DESCRIPTION ("Software Requirement") — GetFilterList matches on
 * it, not on the code ("SRS"), so we hand it over rather than making the backend re-resolve it.
 * `versionId`/`orcanosUrl` are only for the ↗ "Open in Orcanos" link — the listing URL is built
 * from the CODE and the block's OWN version, never the label or L1's version.
 */
export default function FilterSelect({ projectId, itemTypeCode, itemTypeLabel, versionId, orcanosUrl,
                        value, onChange, placeholder, cache, ensure, linkLabel }) {
  // ⚠️ The LABEL is part of the key, not just the (project, type) pair. `getWorkItemLabelFromCode`
  // returns the CODE while the project's type list is still loading, so the first fetch can go out
  // as `label=MR_REQ` — which GetFilterList rejects (it matches on the DESCRIPTION). Keying on the
  // pair alone cached that failure for the life of the page and the picker never came back, with
  // nothing on screen to say why (observed 2026-08-29). With the label in the key, the real label
  // re-fetches as soon as the types land.
  const key = projectId && itemTypeCode
    ? `${projectId}|${itemTypeCode}|${itemTypeLabel || ''}`
    : null;

  useEffect(() => {
    if (key) ensure(projectId, itemTypeCode, itemTypeLabel);
  }, [key, projectId, itemTypeCode, itemTypeLabel, ensure]);

  const entry = key ? cache[key] : null;
  const useCombo = !!(entry && entry.available && entry.filters && entry.filters.length > 0);
  const openLink = (
    <OpenFilterLink
      baseUrl={orcanosUrl}
      versionId={versionId}
      itemTypeCode={itemTypeCode}
      filterId={value}
      label={linkLabel || 'filter'}
    />
  );

  if (!useCombo) {
    return (
      <>
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'e.g., 934'}
        />
        {openLink}
        {/* A failed lookup, a tenant without the API, and a type Orcanos keeps no filters
            for all look identical in this box — say which one it was, or "the picker is
            gone" reads as a missing feature. */}
        {entry && entry.failed && (
          <small style={{ color: '#b45309' }}>
            Couldn't load the filter list (connection problem) — reload the page to try again.
          </small>
        )}
        {entry && !entry.failed && entry.reason === 'unsupported_type' && (
          <small style={{ color: '#6b7280' }}>
            Orcanos keeps no saved filters for {itemTypeLabel || itemTypeCode} — enter the filter ID.
          </small>
        )}
      </>
    );
  }

  // Build label -> id map. Names aren't guaranteed unique, so the id is shown in the label.
  const labelOf = (f) => `${f.name} (#${f.id})`;
  const byLabel = {};
  const options = [''];
  let currentLabel = '';
  entry.filters.forEach((f) => {
    const l = labelOf(f);
    byLabel[l] = f.id;
    options.push(l);
    if (value !== '' && value != null && String(f.id) === String(value)) currentLabel = l;
  });
  // Preserve a saved id that isn't in the returned set (e.g. a private or deleted filter)
  // so editing an existing panel never silently blanks its filter.
  if (value !== '' && value != null && !currentLabel) {
    const l = `#${value} (current)`;
    byLabel[l] = value;
    options.splice(1, 0, l);
    currentLabel = l;
  }

  return (
    <>
      <SearchableSelect
        value={currentLabel}
        onChange={(e) => onChange(byLabel[e.target.value] ?? '')}
        options={options}
        placeholder="Search filters..."
      />
      {openLink}
      {/* Orcanos can't list this type's filters (Test Run), so the list is only the ones
          other panels use — a new filter must still be typable. */}
      {entry.source === 'panels' && (
        <>
          <small style={{ color: '#6b7280' }}>
            Orcanos can't list {itemTypeLabel || itemTypeCode} filters — these are the ones your
            panels use. Or type an id:
          </small>
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || 'filter id'}
          />
        </>
      )}
    </>
  );
}

/** The per-page filter-list cache that every FilterSelect on a page shares. */
export function useFilterCache() {
  // Filter-picker cache, keyed `${projectId}|${itemTypeCode}`. One fetch per pair, shared by
  // every FilterSelect (base + per-level + orphans). The ref mirrors state so `ensureFilters`
  // can stay identity-stable (deps []) and de-dupe in-flight fetches without re-running effects.
  const [filterCache, setFilterCache] = useState({});
  const filterCacheRef = useRef({});

  // Fetch (once) the pickable filters for a (project, item-type) pair. Stable identity so
  // FilterSelect effects don't re-run; de-dupes via the ref so many fields asking for the
  // same pair trigger a single request.
  const ensureFilters = useCallback((projectId, code, label) => {
    if (!projectId || !code) return;
    const key = `${projectId}|${code}|${label || ''}`;   // label included — see FilterSelect
    if (filterCacheRef.current[key]) return;              // already fetched / in flight
    filterCacheRef.current[key] = { loading: true };
    setFilterCache(prev => ({ ...prev, [key]: { loading: true } }));

    // GetFilterList keys on the work-item DESCRIPTION (label), not the code — send it along.
    // The backend resolves it from QW_Login when we can't supply it.
    const labelParam = label ? `&item_type_label=${encodeURIComponent(label)}` : '';
    const url = `/api/config/filters?project_id=${encodeURIComponent(projectId)}&item_type=${encodeURIComponent(code)}${labelParam}`;

    // ⚠️ A 200 saying `available: false` is a real answer (this tenant has no GetFilterList)
    // and is cached. A TRANSPORT failure is not: it means the backend was restarting, the
    // proxy blinked, or the session hiccupped — and caching it silently downgrades every
    // filter field to a manual ID box for the rest of the page's life, with nothing on
    // screen to say why (observed 2026-08-29 on a tenant that had 55 pickable filters).
    // So: retry twice, then FORGET the pair so a later mount can try again.
    const attempt = (n) => {
      fetch(url)
        .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then(d => {
          const entry = {
            loading: false,
            available: !!d.available,
            filters: d.filters || [],
            reason: d.reason || null,   // 'unsupported_type' => Orcanos has no list for this type
            source: d.source || null,   // 'panels' => not Orcanos's list, the filters panels use
          };
          filterCacheRef.current[key] = entry;
          setFilterCache(prev => ({ ...prev, [key]: entry }));
        })
        .catch(() => {
          if (n < 2) {
            setTimeout(() => attempt(n + 1), 1200 * (n + 1));
            return;
          }
          // Fall back to a manual input for now, but drop the ref entry so the next
          // FilterSelect mount for this pair re-fetches instead of inheriting the failure.
          delete filterCacheRef.current[key];
          setFilterCache(prev => ({
            ...prev,
            [key]: { loading: false, available: false, filters: [], failed: true }
          }));
        });
    };
    attempt(0);
  }, []);

  return { filterCache, ensureFilters };
}
